import HexagonLoader from '@/components/HexagonLoader/HexagonLoader';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import UserAvatarSVG from '@/components/UserAvatarSVG';
import '@/styles/ArrearsDashStyles.scss';
import ArrearsLegalData from '../ExecutiveDash/ArrearsLegalData';
import MonthlyArrearsTable from '../ExecutiveDash/MonthlyArrearsTable';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, Legend, ResponsiveContainer } from 'recharts'
import * as Sentry from '@sentry/react';

/**
 * Custom square dot component for LineChart
 */
const SquareDot = (props) => {
    const { cx, cy, fill = "#F63040", stroke = "#F63040" } = props;
    return (
        <rect
            x={cx - 4}
            y={cy - 4}
            width={8}
            height={8}
            fill={fill}
            stroke={stroke}
            strokeWidth={2}
            rx={0}
            ry={0}
        />
    );
};

const badDebtsList = [
    "Provision for Bad Debts",
    "Bad debts written off",
    "Bad Debts Recovered"
];

const incomeList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Insurance Income - Revenue",
    "Covid Credits - Rental",
    "Other Income",
    "Other Contractual Income",
    "Promotional Income",
];

const billingsList = [
    "Basic Contractual Income",
    "Rent Turnover Income",
    "Other Contractual Income",
    "Promotional Income",
    "Rates Income",
    "Electricity Recovered",
    "Water Recovered",
    "Sewerage Recovered ",
    "Refuse Recovered"
];

/**
 * Pure helper — sums values for matching captions from a dataset.
 * Extracted outside the component so it has a stable identity and
 * can be called from useMemo without triggering dependency changes.
 *
 * @param {Array} data - The condensed income data array
 * @param {Array} includedCaptions - Caption names to include
 * @param {string} valueProperty - Property name for values
 * @returns {number} Total value (sign-inverted per existing convention)
 */
function sumCaptions(data, includedCaptions, valueProperty) {
    if (!Array.isArray(data) || !Array.isArray(includedCaptions) || !valueProperty) {
        return 0;
    }
    return data.reduce((sum, item) => {
        if (includedCaptions.includes(item.Caption)) {
            const value = typeof item[valueProperty] === 'number'
                ? item[valueProperty]
                : parseFloat(item[valueProperty] || '0');
            return sum + (value * -1);
        }
        return sum;
    }, 0);
}

/**
 * Helper function to extract month and year from period string
 * @param {string|number} period - Period in format YYYYMM
 * @returns {Object} Object with monthName, year, and prevYear
 */
function getMonthYear(period) {
    if (!period) return { monthName: '', year: '', prevYear: '' };
    const periodStr = period.toString().padStart(6, '0');
    if (periodStr.length !== 6) return { monthName: '', year: '', prevYear: '' };
    const year = periodStr.substring(0, 4);
    const prevYear = (parseInt(year, 10) - 1).toString();
    const month = periodStr.substring(4, 6);
    const monthNames = [
        '', 'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthIndex = parseInt(month, 10);
    return {
        monthName: monthNames[monthIndex],
        year,
        prevYear
    };
}

const ArrearsDashboard = () => {
    const { apiFetch, user } = useAuth();
    const [initialLoading, setInitialLoading] = useState(true);
    const [period, setPeriod] = useState(null);
    const [condensedIncomeData, setCondensedIncomeData] = useState(null);
    const [condensedIncomeDataPrev, setCondensedIncomeDataPrev] = useState(null);
    const [topArrears, setTopArrears] = useState({
        Legal: [],
        NonLegal: []
    });
    const [billingData, setBillingData] = useState([]);
    const [prevArrearsData, setPrevArrearsData] = useState({});
    const [paidToSellerAmounts, setPaidToSellerAmounts] = useState({});

    // Ref for aborting in-flight fetches on unmount / period change
    const abortRef = useRef(null);

    // ── Derived calculations via useMemo (no re-render loop) ────────────
    const badDebtPercentages = useMemo(() => {
        if (!condensedIncomeData) {
            return { percentageCurrent: "0.0%", percentageYTD: "0.0%", percentagePrev: "0.0%" };
        }

        // Current Month
        const badDebtsCurrent = sumCaptions(condensedIncomeData, badDebtsList, "CurrentMovements");
        const totalIncomeCurrent = sumCaptions(condensedIncomeData, incomeList, "CurrentMovements");
        const finalCurrentRatio = totalIncomeCurrent !== 0 ? -badDebtsCurrent / totalIncomeCurrent : 0;
        const percentageCurrent = (finalCurrentRatio * 100).toLocaleString('en-ZA', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
        }) + '%';

        // YTD
        const badDebtsYTD = sumCaptions(condensedIncomeData, badDebtsList, "CurrentBal");
        const totalIncomeYTD = sumCaptions(condensedIncomeData, incomeList, "CurrentBal");
        const finalYTDRatio = totalIncomeYTD !== 0 ? -badDebtsYTD / totalIncomeYTD : 0;
        const percentageYTD = (finalYTDRatio * 100).toLocaleString('en-ZA', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
        }) + '%';

        // Prev Year
        const prevData = condensedIncomeDataPrev;
        const badDebtsPrev = sumCaptions(prevData, badDebtsList, "BudgetFullYearRevised");
        const totalIncomePrev = sumCaptions(prevData, incomeList, "BudgetFullYearRevised");
        const finalPrevRatio = totalIncomePrev !== 0 ? -badDebtsPrev / totalIncomePrev : 0;
        const percentagePrev = (finalPrevRatio * 100).toLocaleString('en-ZA', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
        }) + '%';

        return { percentageCurrent, percentageYTD, percentagePrev };
    }, [condensedIncomeData, condensedIncomeDataPrev]);

    const billings = useMemo(() => {
        if (!condensedIncomeData || !period) {
            return { monthlyBillingsCurrent: 0, monthlyBillingsYTD: 0, monthlyBillingsPrev: 0 };
        }

        const periodStr = period.toString().padStart(6, '0');
        let monthNum = parseInt(periodStr.slice(-2), 10);
        if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) monthNum = 1;

        const monthlyBillingsCurrent = sumCaptions(condensedIncomeData, billingsList, "CurrentMovements");

        const monthlyBillingsYTD = billingsList.reduce(
            (total, caption) => total + sumCaptions(condensedIncomeData, [caption], "CurrentBal"), 0
        ) / monthNum;

        const prevData = condensedIncomeDataPrev;
        const monthlyBillingsPrev = billingsList.reduce(
            (total, caption) => total + sumCaptions(prevData, [caption], "CurrentBal"), 0
        ) / 12;

        return { monthlyBillingsCurrent, monthlyBillingsYTD, monthlyBillingsPrev };
    }, [condensedIncomeData, condensedIncomeDataPrev, period]);

    // ── Data fetching ───────────────────────────────────────────────────

    const fetchCondensedIncomeStatement = useCallback(async (signal) => {
        try {
            const response = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${period}`, { signal });
            const data = await response.json();
            if (data.success) {
                setCondensedIncomeData(data.data);
            } else {
                console.error('Failed to fetch trial balance data:', data.message);
            }
        } catch (err) {
            Sentry.captureException(err);
            if (err.name !== 'AbortError' && err.message !== 'Token refresh failed') {
                console.error('Error fetching trial balance data:', err);
            }
        }
    }, [apiFetch, period]);

    const fetchPrevCondensedIncomeStatement = useCallback(async (signal) => {
        try {
            const { prevYear } = getMonthYear(period);
            const prevDecember = `${prevYear}12`;
            const prevDecResponse = await apiFetch(`/api/Mda/CondensedIncomeStatementData?Period=${prevDecember}`, { signal });
            const prevDecData = await prevDecResponse.json();
            if (prevDecData.success) {
                setCondensedIncomeDataPrev(prevDecData.data);
            } else {
                console.error('Failed to fetch previous December data:', prevDecData.message);
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.name !== 'AbortError') console.error(e);
        }
    }, [apiFetch, period]);

    const fetchTop10LegalOrNonLegalArrears = useCallback(async (signal) => {
        try {
            const response = await apiFetch(`/api/Mda/TopLegalAndNonLegalArrears?Period=${period}`, { signal });
            const data = await response.json();
            if (data.success && data.data) {
                setTopArrears({
                    Legal: data.data.legal ?? [],
                    NonLegal: data.data.nonLegal ?? []
                });
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.name !== 'AbortError') console.error(e);
        }
    }, [apiFetch, period]);

    const fetchArrearsFor12PrevPeriods = useCallback(async (signal) => {
        try {
            const response = await apiFetch(`/api/Mda/GetArrearsForLast12Periods?period=${period}`, { signal });
            const data = await response.json();
            if (data.success) {
                setPrevArrearsData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.name !== 'AbortError') console.error(e);
        }
    }, [apiFetch, period]);

    const fetchBillings = useCallback(async (signal) => {
        try {
            const response = await apiFetch(`/api/Mda/SumInclusiveAmountByPeriod?period=${period}`, { signal });
            const data = await response.json();
            if (data.success) {
                setBillingData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.name !== 'AbortError') console.error(e);
        }
    }, [apiFetch, period]);

    const fetchPaidToSellerAmounts = useCallback(async (signal) => {
        try {
            const response = await apiFetch(`/api/Leasing/PaidToSeller13MonthRange?period=${period}`, { signal });
            const data = await response.json();
            if (data.success) {
                setPaidToSellerAmounts(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            if (e.name !== 'AbortError') console.error(e);
        }
    }, [apiFetch, period]);

    useEffect(() => {
        const controller = new AbortController();
        const fetchDefaultPeriod = async () => {
            try {
                const response = await apiFetch(`/api/Mda/DefaultPeriod`, { signal: controller.signal });
                const data = await response.json();
                setPeriod(data.period1);
            } catch (e) {
                Sentry.captureException(e);
                if (e.name !== 'AbortError' && e.message !== 'Token refresh failed') {
                    console.error(e);
                }
            }
        };
        fetchDefaultPeriod();
        return () => controller.abort();
    }, [apiFetch]);

    useEffect(() => {
        if (!period) return;

        // Abort any in-flight requests from the previous period / render
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const { signal } = controller;

        // eslint-disable-next-line react-hooks/set-state-in-effect
        fetchCondensedIncomeStatement(signal);
        fetchPrevCondensedIncomeStatement(signal);
        fetchTop10LegalOrNonLegalArrears(signal);
        fetchArrearsFor12PrevPeriods(signal);
        fetchBillings(signal);
        fetchPaidToSellerAmounts(signal);

        return () => controller.abort();
    }, [period, fetchCondensedIncomeStatement, fetchPrevCondensedIncomeStatement, fetchTop10LegalOrNonLegalArrears, fetchArrearsFor12PrevPeriods, fetchBillings, fetchPaidToSellerAmounts]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setInitialLoading(false);
        }, 3000);

        return () => clearTimeout(timer);
    }, []);

    // ── Rendering helpers ───────────────────────────────────────────────

    const { monthName, year } = getMonthYear(period);

    // Helper to format with regular spaces as thousands separator
    function formatNumberWithSpaces(num) {
        return Math.round(num)
            .toLocaleString('fr-FR')
            .replace(/[\u202F\u00A0 ]/g, ' ');
    }

    const renderArrearsTable = (arrearsList, title) => {
        const totalDue = arrearsList.reduce((sum, item) => sum + Math.round(item.taxExclusiveAmount), 0);
        const totalProvision = totalDue;

        return (
            <div className="arrears-table-container">
                <table className="arrears-table">
                    <thead>
                        <tr>
                            <th>{title}</th>
                            <th>Tenant</th>
                            <th>
                                Total Due<br />
                                {monthName} {year}
                            </th>
                            <th>
                                Provision<br />
                                {monthName} {year}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {arrearsList.map((item, idx) => (
                            <tr key={idx}>
                                <td>{item.propertyName ?? ''}</td>
                                <td>{item.tenantName}</td>
                                <td>{formatNumberWithSpaces(item.taxExclusiveAmount)}</td>
                                <td>{formatNumberWithSpaces(item.taxExclusiveAmount)}</td>
                            </tr>
                        ))}
                        <tr className="arrears-table-total-row">
                            <td><strong>Total</strong></td>
                            <td></td>
                            <td>
                                <strong>
                                    <span className="total-due-align">{formatNumberWithSpaces(totalDue)}</span>
                                </strong>
                            </td>
                            <td><strong>{formatNumberWithSpaces(totalProvision)}</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    };

    function labelToPeriod(label) {
        const [monthStr, yearStr] = label.split(' ');
        const monthMap = {
            Jan: '01', Feb: '02', Mar: '03', Apr: '04', May: '05', Jun: '06',
            Jul: '07', Aug: '08', Sep: '09', Sept: '09', Oct: '10', Nov: '11', Dec: '12'
        };
        return Number(yearStr + monthMap[monthStr]);
    }

    const monthLabels = Object.keys(prevArrearsData);
    const graphData = monthLabels.map(label => {
        const period = labelToPeriod(label);
        const sortedBillings = [...billingData].sort((a, b) => a.period - b.period);
        const idx = sortedBillings.findIndex(b => b.period === period);
        const slice = sortedBillings.slice(Math.max(0, idx - 11), idx + 1);
        const billingsSumInclusive = slice.reduce((sum, item) => sum + (item.totalInclusiveAmount || 0), 0);
        const billingsSumExclusive = billingsSumInclusive / 1.15;

        const paidToSeller = paidToSellerAmounts && paidToSellerAmounts[period] ? paidToSellerAmounts[period] : 0;
        const adjustedArrears = (prevArrearsData[label] || 0) - (paidToSeller || 0);

        const percent = billingsSumExclusive !== 0 ? (adjustedArrears / billingsSumExclusive) * 100 : 0;

        return { x: label, y: percent };
    });
    const averagePercent = graphData.length ? graphData.reduce((sum, d) => sum + d.y, 0) / graphData.length : 0;

    return (
        <>
            {initialLoading ? (
                <HexagonLoader />
            ) : (
                <div className="arrears-dash-grid">
                    <div className="arrears-dash-header" style={{ gridColumn: '1 / span 2' }}>
                        <h1 className="dashboard-greeting">
                            <UserAvatarSVG />
                            Hello {user.FirstName || user.firstName}
                        </h1>
                        <h3>Arrears Dashboard</h3>
                    </div>
                    <div className="arrears-dash-table">
                        <ArrearsLegalData period={period} badDebtPercentages={badDebtPercentages} billings={billings} hideLegalRows={true} />
                    </div>
                    <div className="arrears-dash-empty"></div>
                    <div className="arrears-dash-graph-left">
                        <MonthlyArrearsTable period={period} />
                    </div>
                    <div className="arrears-dash-graph-right">
                        <div className="graph-heading-arrears-new">
                            Normalised Arrears as % of Billings Graph
                        </div>
                            <div className="graph-data-arrears">
                                <ResponsiveContainer width="100%" height={240}>
                                    <LineChart data={graphData}>
                                        <CartesianGrid stroke="#000" vertical={false} fill="#f5f5f5" />
                                        <XAxis
                                            dataKey="x"
                                            dy={2}
                                            tick={{ fontSize: 12 }}
                                            tickFormatter={(label) => {
                                                const parts = label.split(' ');
                                                if (parts.length === 2) {
                                                    return `${parts[0]} ${parts[1].slice(-2)}`;
                                                }
                                                return label;
                                            }}
                                        />
                                        <YAxis
                                            domain={[1.2, 2.4]}
                                            ticks={[1.2, 1.4, 1.6, 1.8, 2.0, 2.2, 2.4]}
                                        />
                                        <Tooltip formatter={value => (Math.round(value * 10) / 10).toFixed(1)} />
                                        <Legend
                                            verticalAlign="bottom"
                                            align="center"
                                            wrapperStyle={{ paddingTop: 10, paddingLeft: 30 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="y"
                                            stroke="#f63040"
                                            strokeWidth={2}
                                            dot={<SquareDot fill="#f63040" stroke="#f63040" />}
                                            name="Normalised arrears as percentage of billings ex VAT"
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="Linear"
                                            stroke="#57565b"
                                            strokeWidth={2}
                                            dot={false}
                                            isAnimationActive={false}
                                            name="Linear (Normalised arrears as percentage of billings ex VAT)"
                                        />
                                        <ReferenceLine y={averagePercent} stroke="#57565b" strokeDasharray="3 3" strokeWidth={2} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                    </div>
                    <div className="arrears-dash-table-bottom-left">
                        {renderArrearsTable(topArrears.Legal, "Top 10 Legal Arrears")}
                    </div>
                    <div className="arrears-dash-table-bottom-right">
                        {renderArrearsTable(topArrears.NonLegal, "Top 10 Non Legal Arrears")}
                    </div>
                </div>
            )}
        </>
    );
}

export default ArrearsDashboard;